import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Briefcase,
  Calendar,
  DollarSign,
  FileText,
  Phone,
  BookmarkCheck,
  AlertCircle,
  ChevronDown,
  Search,
} from "lucide-react";
import styles from "./OfertaDeTrabajo.module.scss";
import { connect } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import JurisdictionList from "../../components/UI/JurisdictionList/JurisdictionList";
import { getSkills, getCities, createWorkOffer, openMainModalDashboard, closeMainModal } from "../../store/actions";
import useGetTodayDate from "../../hooks/useGetTodayDate";
import { fireAnalytics } from "../../utils/firebase";
import Breadcrumbs from "../../components/UI/Breadcrumbs/Breadcrumbs.tsx";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import environment from "../../environments";
import TurnstileWidget from "../../components/UI/TurnstileWidget/TurnstileWidget.tsx";
import type { TurnstileWidgetHandle } from "../../components/UI/TurnstileWidget/TurnstileWidget.tsx";
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import { useActionGuard } from "../../hooks/useActionGuard.ts";

type ContactMethod = "email" | "phone";
type SalaryType = "Hour" | "Day" | "Week" | "Month" | "Job";

interface Skill {
  id: number;
  name: string;
}

interface City {
  name: string;
}

interface WorkZoneItem {
  jurisdiccion: number;
  name: string;
  city: string;
}

interface Jurisdiction {
  id: number;
  name: string;
}

interface UserData {
  id: number;
  username?: string;
  phone?: string;
}

interface WorkOfferFormValues {
  selectedJurisdiction: Jurisdiction | null;
  workZone: WorkZoneItem[];
  skills: Skill[];
  description: string;
  inmediateStart: boolean;
  startDate: string;
  contactMethod: ContactMethod;
  alternativeContact: string;
  phone: string | undefined;
  salary: number | "";
  salaryFormatted: string;
  activeUntil: string;
  salaryType: SalaryType;
  city: string;
}

interface OfertaDeTrabajoProps {
  isStandalone?: boolean;
  cities: City[];
  skills: Skill[];
  userData: UserData | null;
  getSkills: () => void;
  getCities: (jurisdictionId: number) => void;
  createWorkOffer: (payload: Record<string, unknown>) => void;
}

interface SelectedItemsListProps {
  items: WorkZoneItem[] | Skill[];
  onRemove: (id: number | string) => void;
  icon: typeof MapPin;
  emptyMessage: string;
}

const itemVariants = {
  initial: {
    opacity: 0,
    scale: 0.95,
    y: -6,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 22,
      mass: 0.8,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -6,
    transition: {
      duration: 0.18,
      ease: "easeIn",
    },
  },
};

const emptyStateVariants = {
  initial: { opacity: 0, scale: 0.96 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.28,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: {
      duration: 0.16,
      ease: "easeIn",
    },
  },
};

const containerVariants = {
  initial: { opacity: 0.8 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
      staggerChildren: 0.04,
    },
  },
  exit: {
    opacity: 0.8,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const SelectedItemsList = ({ items, onRemove, icon: Icon, emptyMessage }: SelectedItemsListProps) => (
  <motion.div
    className={styles.selectedItemsContainer}
    variants={containerVariants}
    initial="initial"
    animate="animate"
    layout
  >
    {items.length === 0 ? (
      <motion.div
        className={styles.selectedItemsEmpty}
        variants={emptyStateVariants}
        initial="initial"
        animate="animate"
        layout
      >
        <Icon size={14} />
        {emptyMessage}
      </motion.div>
    ) : (
      <motion.div className={styles.selectedItems} layout>
        {items.map((item, index) => {
          const entry = item as WorkZoneItem & Skill;
          const key = entry.id ?? `${entry.jurisdiccion}-${entry.city}-${index}`;
          return (
            <motion.button
              key={key}
              type="button"
              className={styles.selectedItem}
              variants={itemVariants}
              initial="initial"
              animate="animate"
              layout
              onClick={() => onRemove(entry.id ?? index)}
              whileTap={{ scale: 0.97, transition: { duration: 0.08 } }}
            >
              <Icon size={12} />
              {entry.city ? `${entry.name} · ${entry.city.split(",")[0]}` : entry.name}
            </motion.button>
          );
        })}
      </motion.div>
    )}
  </motion.div>
);

const OfertaDeTrabajo = ({
  isStandalone = false,
  cities,
  skills,
  userData,
  getSkills,
  getCities,
  createWorkOffer,
}: OfertaDeTrabajoProps) => {
  const [skillSearchValue, setSkillSearchValue] = useState<string>("");
  const [showSkillsList, setShowSkillsList] = useState<boolean>(false);
  const [salaryType, setSalaryType] = useState<SalaryType>("Hour");
  const [captchaReady, setCaptchaReady] = useState(false);
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const { guard } = useActionGuard();
  const today = useGetTodayDate();
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const useCaptcha = Boolean(isStandalone && environment.turnstile?.siteKey);
  const captchaReadyForSubmit = !useCaptcha || captchaReady;

  const convertDateToISOFormat = useCallback((date: string) => {
    const [dd, mm, yyyy] = date.split("/");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const startDateInitialValue = useMemo(() => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  }, []);

  const initialValues: WorkOfferFormValues = useMemo(
    () => ({
      selectedJurisdiction: null,
      workZone: [],
      skills: [],
      description: "",
      inmediateStart: true,
      startDate: startDateInitialValue,
      contactMethod: "email",
      alternativeContact: userData?.username ?? "",
      phone: userData?.phone,
      salary: 0,
      salaryFormatted: "",
      activeUntil: "",
      salaryType,
      city: "",
    }),
    [startDateInitialValue, userData, salaryType]
  );

  const formSchema = Yup.object().shape({
    selectedJurisdiction: Yup.object().nullable(),
    workZone: Yup.array().min(1, "Debes seleccionar al menos una ciudad"),
    inmediateStart: Yup.boolean().required("Debes indicar si el inicio es inmediato"),
    activeUntil: Yup.string().required("Debes ingresar la fecha de finalización de la oferta"),
    skills: Yup.array().min(1, "*Debes seleccionar al menos un rubro de trabajo"),
  });

  const formik = useFormik<WorkOfferFormValues>({
    validateOnMount: true,
    validateOnBlur: true,
    initialValues,
    validationSchema: formSchema,
    onSubmit: async (values, helpers) => {
      if (useCaptcha) {
        const token = turnstileTokenRef.current;
        if (!token) return;
        await createWorkOffer({ ...values, captcha: token });
        helpers.resetForm();
        setCaptchaReady(false);
        turnstileTokenRef.current = null;
        turnstileRef.current?.reset();
        return;
      }

      await createWorkOffer({ ...values, captcha: undefined });
      helpers.resetForm();
    },
  });

  const {
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    setFieldValue,
    handleChange,
    handleSubmit: submitForm,
  } = formik;

  const handleTurnstileVerify = useCallback((token: string) => {
    turnstileTokenRef.current = token;
    setCaptchaReady(true);
  }, []);

  const handleTurnstileError = useCallback(() => {
    turnstileTokenRef.current = null;
    setCaptchaReady(false);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    turnstileTokenRef.current = null;
    setCaptchaReady(false);
  }, []);

  const showCaptcha = useCaptcha && environment.turnstile.siteKey && isValid;

  useEffect(() => {
    if (!showCaptcha) setCaptchaReady(false);
  }, [showCaptcha]);

  useEffect(() => {
    getSkills();
    if (userData?.id) {
      
      // Nuevo evento estandarizado
      fireAnalytics("work_offer_modal_opened", {
        edify_user_id: userData.id,
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "work_offers",
        action: "modal_opened"
      });
    }
  }, [getSkills, userData]);

  useEffect(() => {
    if (values.selectedJurisdiction) {
      getCities(values.selectedJurisdiction.id);
    }
  }, [values.selectedJurisdiction, getCities]);

  useEffect(() => {
    if (values.inmediateStart) {
      setFieldValue("startDate", convertDateToISOFormat(today));
    }
  }, [values.inmediateStart, convertDateToISOFormat, setFieldValue, today]);

  const filteredSkills = useMemo(
    () =>
      skills.filter((skill) => skill.name.toLowerCase().includes(skillSearchValue.toLowerCase())),
    [skills, skillSearchValue]
  );

  const handleAddSkill = useCallback(
    (skill: Skill) => {
      if (!skill?.id) return;
      const exists = values.skills.some((current) => current.id === skill.id);
      if (!exists) {
        setFieldValue("skills", [...values.skills, { id: skill.id, name: skill.name }]);
      }
      setSkillSearchValue("");
      setShowSkillsList(false);
    },
    [values.skills, setSkillSearchValue, setShowSkillsList, setFieldValue]
  );

  const handleRemoveSkill = useCallback(
    (skillId: number | string) => {
      setFieldValue(
        "skills",
        values.skills.filter((skill) => skill.id !== skillId)
      );
    },
    [values.skills, setFieldValue]
  );

  const handleAddWorkZone = useCallback(
    (city: City) => {
      if (!values.selectedJurisdiction) return;
      const entry: WorkZoneItem = {
        jurisdiccion: values.selectedJurisdiction.id,
        name: values.selectedJurisdiction.name,
        city: city.name,
      };
      setFieldValue("workZone", [...values.workZone, entry]);
      setFieldValue("city", city.name);
      setFieldValue("selectedJurisdiction", null);
    },
    [values.workZone, values.selectedJurisdiction, setFieldValue]
  );

  const handleRemoveWorkZone = useCallback(
    (index: number | string) => {
      setFieldValue(
        "workZone",
        values.workZone.filter((_, currentIndex) => currentIndex !== Number(index))
      );
    },
    [values.workZone, setFieldValue]
  );

  const handleSalaryTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value as SalaryType;
      setSalaryType(value);
      setFieldValue("salaryType", value);
    },
    [setFieldValue]
  );

  const handleSalaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/\./g, "").replace(/\D/g, "");
      if (raw) {
        const numberValue = Number.parseInt(raw, 10);
        if (!Number.isNaN(numberValue)) {
          const formatted = new Intl.NumberFormat("es-ES").format(numberValue);
          setFieldValue("salary", numberValue);
          setFieldValue("salaryFormatted", formatted);
        }
      } else {
        setFieldValue("salary", "");
        setFieldValue("salaryFormatted", "");
      }
    },
    [setFieldValue]
  );

  const handleContactMethod = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value as ContactMethod;
      setFieldValue("contactMethod", value);
      if (value === "phone") {
        setFieldValue("alternativeContact", userData?.phone ?? "");
      } else {
        setFieldValue("alternativeContact", userData?.username ?? "");
      }
    },
    [setFieldValue, userData]
  );

  const isFieldComplete = useCallback(
    (fieldName: "workZone" | "skills" | "activeUntil") => {
      if (fieldName === "workZone") return values.workZone.length > 0;
      if (fieldName === "skills") return values.skills.length > 0;
      if (fieldName === "activeUntil") return values.activeUntil !== "";
      return false;
    },
    [values]
  );

  const handleSubmit = useCallback(() => {
    submitForm();
  }, [submitForm]);

  const renderFormSections = () => (
    <>
      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <MapPin size={18} />
          <div>
            <h3 className={styles.sectionTitle}>
              Ubicación
              {!isFieldComplete("workZone") && <span className={styles.required}>*</span>}
            </h3>
            <span className={styles.sectionHint}>Define provincias y ciudades donde se realizará el trabajo</span>
          </div>
        </header>

        <SelectedItemsList
          items={values.workZone}
          onRemove={handleRemoveWorkZone}
          icon={MapPin}
          emptyMessage="No hay ubicaciones seleccionadas"
        />

        <div className={styles.fieldGridTwo}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>
              Provincia
              {!isFieldComplete("workZone") && <span className={styles.required}>*</span>}
            </label>
            <select
              className={`${styles.select} ${
                touched.workZone && errors.workZone ? styles.inputError : ""
              }`}
              value={values.selectedJurisdiction?.id ?? ""}
              onChange={(event) => {
                const selected = JurisdictionList.find(
                  (jurisdiction) => jurisdiction.id === Number.parseInt(event.target.value, 10)
                );
                setFieldValue("selectedJurisdiction", selected ?? null);
              }}
            >
              <option value="">Seleccionar provincia</option>
              {JurisdictionList.map((jurisdiction: Jurisdiction) => (
                <option key={jurisdiction.id} value={jurisdiction.id}>
                  {jurisdiction.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>
              Ciudad
              {!isFieldComplete("workZone") && <span className={styles.required}>*</span>}
            </label>
            <select
              className={`${styles.select} ${
                touched.workZone && errors.workZone ? styles.inputError : ""
              }`}
              disabled={!values.selectedJurisdiction}
              value=""
              onChange={(event) => {
                const city = cities.find((entry) => entry.name === event.target.value);
                if (city) handleAddWorkZone(city);
              }}
            >
              <option value="">Seleccionar ciudad</option>
              {cities.map((city) => (
                <option key={city.name} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
            {touched.workZone && errors.workZone && (
              <div className={styles.fieldError}>
                <AlertCircle size={14} />
                {errors.workZone as string}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <Briefcase size={18} />
          <div>
            <h3 className={styles.sectionTitle}>
              Rubro
              {!isFieldComplete("skills") && <span className={styles.required}>*</span>}
            </h3>
            <span className={styles.sectionHint}>Selecciona los rubros o habilidades requeridas</span>
          </div>
        </header>

        <SelectedItemsList
          items={values.skills}
          onRemove={handleRemoveSkill}
          icon={Briefcase}
          emptyMessage="No hay rubros seleccionados"
        />

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Seleccionar rubro
            {!isFieldComplete("skills") && <span className={styles.required}>*</span>}
          </label>
          <div className={styles.skillSelector}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                placeholder="Buscar rubro..."
                value={skillSearchValue}
                onChange={(event) => {
                  const value = event.target.value;
                  setSkillSearchValue(value);
                  setShowSkillsList(value.length > 0);
                }}
                className={`${styles.input} ${
                  touched.skills && errors.skills ? styles.inputError : ""
                }`}
              />
              <Search size={16} className={styles.searchIcon} />
              <button
                type="button"
                className={`${styles.dropdownTrigger} ${showSkillsList ? styles.dropdownOpen : ""}`}
                onClick={() => setShowSkillsList(!showSkillsList)}
              >
                <ChevronDown size={16} />
              </button>
            </div>

            {showSkillsList && (
              <motion.div
                className={styles.dropdown}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {filteredSkills.length > 0 ? (
                  filteredSkills.map((skill) => (
                    <button
                      key={skill.id}
                      type="button"
                      className={styles.dropdownItem}
                      onClick={() => handleAddSkill(skill)}
                    >
                      {skill.name}
                    </button>
                  ))
                ) : (
                  <div className={styles.dropdownItemMuted}>
                    {skillSearchValue ? "No se encontraron rubros" : "Cargando rubros..."}
                  </div>
                )}
              </motion.div>
            )}
          </div>
          {touched.skills && errors.skills && (
            <div className={styles.fieldError}>
              <AlertCircle size={14} />
              {errors.skills as string}
            </div>
          )}
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <Calendar size={18} />
          <div>
            <h3 className={styles.sectionTitle}>
              Fechas
              {!isFieldComplete("activeUntil") && <span className={styles.required}>*</span>}
            </h3>
            <span className={styles.sectionHint}>Define la vigencia de la publicación y el inicio del trabajo</span>
          </div>
        </header>

        <div className={styles.dateGrid}>
          <div className={styles.dateColumn}>
            <h4 className={styles.dateTitle}>Fecha de inicio del trabajo</h4>
            <div className={styles.checkboxRow}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={values.inmediateStart}
                  onChange={handleChange}
                  name="inmediateStart"
                  className={styles.checkbox}
                />
                Inicio inmediato
              </label>
            </div>

            {!values.inmediateStart && (
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Fecha específica de inicio</label>
                <div className={styles.dateInput}>
                  <input
                    type="date"
                    className={styles.input}
                    min={convertDateToISOFormat(today)}
                    max={values.activeUntil}
                    value={values.startDate}
                    onChange={handleChange}
                    name="startDate"
                  />
                </div>
              </div>
            )}
          </div>

          <div className={`${styles.dateColumn} ${styles.dateColumnHighlight}`}>
            <h4 className={styles.dateTitle}>Visibilidad de la oferta</h4>
            <p className={styles.dateDescription}>
              Seleccioná el último día en el que querés que tu aviso reciba postulaciones. Ese día la publicación se
              cerrará automáticamente.
            </p>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Último día visible
                {!isFieldComplete("activeUntil") && <span className={styles.required}>*</span>}
              </label>
              <div className={styles.dateInput}>
                <input
                  type="date"
                  className={`${styles.input} ${
                    touched.activeUntil && errors.activeUntil ? styles.inputError : ""
                  }`}
                  min={values.startDate || convertDateToISOFormat(today)}
                  value={values.activeUntil}
                  onChange={handleChange}
                  name="activeUntil"
                />
              </div>
              {touched.activeUntil && errors.activeUntil && (
                <div className={styles.fieldError}>
                  <AlertCircle size={14} />
                  {errors.activeUntil as string}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <DollarSign size={18} />
          <div>
            <h3 className={styles.sectionTitle}>Remuneración</h3>
            <span className={styles.sectionHint}>Define el monto y la modalidad de pago ofrecida</span>
          </div>
        </header>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>Monto ofrecido</label>
          <div className={styles.salaryRow}>
            <span className={styles.currencySymbol}>$</span>
            <input
              type="text"
              placeholder="Ingresar monto"
              className={styles.salaryInput}
              value={values.salaryFormatted}
              onChange={handleSalaryChange}
              name="salary"
            />
            <select
              value={values.salaryType}
              onChange={handleSalaryTypeChange}
              className={styles.salarySelect}
              name="salaryType"
            >
              <option value="Hour">por Hora</option>
              <option value="Day">por Día</option>
              <option value="Week">por Semana</option>
              <option value="Month">por Mes</option>
              <option value="Job">Trabajo completo</option>
            </select>
          </div>
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <FileText size={18} />
          <div>
            <h3 className={styles.sectionTitle}>Descripción</h3>
            <span className={styles.sectionHint}>
              Describe responsabilidades, requisitos y beneficios del puesto
            </span>
          </div>
        </header>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>Detalles de la oferta</label>
          <textarea
            placeholder="Describe los requisitos, responsabilidades, beneficios y cualquier información relevante sobre la posición..."
            className={styles.textarea}
            value={values.description}
            onChange={handleChange}
            name="description"
            rows={6}
          />
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <Phone size={18} />
          <div>
            <h3 className={styles.sectionTitle}>Información de contacto</h3>
            <span className={styles.sectionHint}>Selecciona cómo querés recibir las postulaciones</span>
          </div>
        </header>

        <div className={styles.fieldGridTwo}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Método de contacto</label>
            <select
              value={values.contactMethod}
              onChange={handleContactMethod}
              className={styles.select}
              name="contactMethod"
            >
              <option value="email">Email</option>
              <option value="phone">Teléfono</option>
            </select>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Contacto</label>
            <input
              type="text"
              placeholder="Ingresar contacto"
              value={values.alternativeContact}
              onChange={handleChange}
              className={styles.input}
              name="alternativeContact"
            />
          </div>
        </div>
      </section>
    </>
  );

  const formContent = (
    <div className={styles.formShell}>
      {renderFormSections()}
      <div className={styles.submitArea}>
        {showCaptcha && (
          <div className={styles.turnstileWrapper}>
            <TurnstileWidget
              ref={turnstileRef}
              sitekey={environment.turnstile.siteKey}
              onVerify={handleTurnstileVerify}
              onExpire={handleTurnstileExpire}
              onError={handleTurnstileError}
              appearance="interaction-only"
              size="normal"
            />
          </div>
        )}
        <button className={styles.submitButton} onClick={guard("create_work_offer", handleSubmit)} disabled={!isValid || !captchaReadyForSubmit}>
          <BookmarkCheck size={18} />
          {isSubmitting
          ? (useCaptcha ? "Verificando..." : "Publicando...")
          : "Publicar oferta de trabajo"}
        </button>
        {!isValid && (
          <div className={styles.formError}>
            <AlertCircle size={16} />
            Por favor, completa todos los campos obligatorios marcados con *
          </div>
        )}
      </div>
    </div>
  );

  if (isStandalone) {
    return (
      <div className={styles.page}>
        <div className={`${styles.wrapper} ${styles.standaloneWrapper}`}>
          <header className={styles.headerStandalone}>
            <h1 className={styles.titleStandalone}>Crear oferta de trabajo</h1>
            <p className={styles.subtitleStandalone}>Completa la información para publicar tu oferta</p>
          </header>
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <div className={styles.wrapper}>
        <header className={styles.header}>
          <Breadcrumbs />
          <p className={styles.subtitle}>Completa la información para publicar una oferta de trabajo</p>
        </header>
        {formContent}
      </div>
    </div>
  );
};

const mapStateToProps = (state: Record<string, any>) => ({
  skills: state.register.skills,
  cities: state.register.cities,
  userData: state.general.userData,
});

export default connect(mapStateToProps, {
  getSkills,
  getCities,
  createWorkOffer,
  openMainModalDashboard,
  closeMainModal,
})(OfertaDeTrabajo);

